// @ts-check
/**
 * @typedef {import('./types').OssPolicyResponse} OssPolicyResponse
 */

/**
 * @typedef {Object} UploadResult
 * @property {string} url
 * @property {string} filename
 */

// OSS (Object Storage Service) API functions
import { apiRequest } from './base';
import { logger } from '../../utils/logger';

const MIME_TO_EXTENSION = {
  mp4: '.mp4',
  aac: '.aac',
  mpeg: '.mp3',
  ogg: '.ogg',
  // The AI tutor converts its recordings to wav before upload, because that is
  // what the speech recogniser reads. Without this entry they would upload as
  // .webm and the recogniser would reject them on the extension alone.
  wav: '.wav',
  webm: '.webm'
};

function getExtensionFromMimeType(mimeType) {
  const type = mimeType || 'audio/webm';
  for (const [key, ext] of Object.entries(MIME_TO_EXTENSION)) {
    if (type.includes(key)) return ext;
  }
  return '.webm';
}

function sanitize(str, allowHyphen = false) {
  const pattern = allowHyphen ? /[^a-zA-Z0-9-]/g : /[^a-zA-Z0-9]/g;
  return (str || '').replace(pattern, '');
}

/** @returns {Promise<OssPolicyResponse>} */
export async function getOSSPolicy(filename, dir) {
  try {
    return await apiRequest('/oss/policy', {
      method: 'POST',
      body: JSON.stringify({ filename, dir }),
    });
  } catch (err) {
    // ONLY 400. That is filename/filetype validation — surfaced to the user as
    // a toast, not a server bug, and not worth reporting.
    //
    // 403 must NOT be swallowed. It means the server refused this caller a
    // permit for that directory, which is either a child wrongly locked out of
    // recording or someone reaching for another student's folder — both things
    // we want to hear about. This used to read `>= 400 && < 500` back when 403
    // was unreachable here; the ownership check on /oss/policy made it
    // reachable, which silently turned this line into a blind spot.
    if (err.statusCode === 400) {
      err.__intentional = true;
    }
    throw err;
  }
}

const DEFAULT_UPLOAD_TIMEOUT_MS = 120000;

/**
 * @param {Object} policyData
 * @param {File|Blob} file
 * @param {{timeoutMs?: number}} [options] - raise for video messages; 120s is
 *   sized for a few-hundred-KB audio clip, not a 150MB phone recording.
 * @returns {Promise<string>}
 */
export async function uploadToOSS(policyData, file, options = {}) {
  if (!policyData || !file) {
    throw new Error('Missing policy data or file for OSS upload');
  }

  const timeoutMs = options.timeoutMs || DEFAULT_UPLOAD_TIMEOUT_MS;

  const formData = new FormData();
  formData.append('key', policyData.key);
  formData.append('OSSAccessKeyId', policyData.accessKeyId);
  formData.append('policy', policyData.policy);
  formData.append('Signature', policyData.signature);
  formData.append('success_action_status', '200');
  formData.append('file', file);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(policyData.host, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = 'Failed to upload file to OSS';
      try {
        const errorText = await response.text();
        const errorMatch = errorText.match(/<Message>(.*?)<\/Message>/);
        if (errorMatch) errorMessage = `OSS Error: ${errorMatch[1]}`;
      } catch {
        // Use default message if parsing fails
      }
      throw new Error(`${errorMessage} (${response.status})`);
    }

    const baseUrl = (policyData.cdnHost || policyData.host).replace(/\/$/, '');
    const key = policyData.key.replace(/^\//, '');
    return `${baseUrl}/${key}`;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('File upload timeout - please try again with a smaller file');
    }

    logger.error('OSS upload failed:', error);
    throw error;
  }
}

const VIDEO_MIME_TO_EXTENSION = {
  quicktime: '.mov',  // iPhone camera roll
  mp4: '.mp4',
  webm: '.webm',
};

// Mirrors VIDEO_MESSAGE_MAX_BYTES in backend/features/storage/storage.helpers.js.
// Checked here too so a teacher gets a clear message instead of an opaque OSS
// rejection after waiting through a long upload.
export const VIDEO_MESSAGE_MAX_BYTES = 150 * 1024 * 1024;

/**
 * Upload a teacher's video message.
 *
 * Filename must match the backend's video-message pattern
 * (`video-message_<teacher>_<timestamp>.<ext>`) or /oss/policy 400s — that
 * pattern is also what grants this upload the raised size ceiling.
 *
 * @param {File} videoFile
 * @param {string} teacherId
 * @returns {Promise<UploadResult>}
 */
export async function uploadVideoMessage(videoFile, teacherId) {
  if (!videoFile || !teacherId) {
    throw new Error('Missing required parameters for video message upload');
  }

  if (videoFile.size > VIDEO_MESSAGE_MAX_BYTES) {
    const err = new Error('视频太大了，请录制短一点（150MB 以内）');
    // @ts-ignore — flag consumed by errorReporting to skip Sentry noise
    err.__intentional = true;
    throw err;
  }

  const type = videoFile.type || '';
  let extension = '';
  for (const [key, ext] of Object.entries(VIDEO_MIME_TO_EXTENSION)) {
    if (type.includes(key)) { extension = ext; break; }
  }
  // Fall back to the file's own extension (some Android pickers report an
  // empty MIME type), then to .mp4 — the pattern rejects anything else anyway.
  if (!extension) {
    const named = (videoFile.name || '').toLowerCase().match(/\.(mp4|mov|webm)$/);
    extension = named ? named[0] : '.mp4';
  }

  const safeTeacher = sanitize(teacherId) || 'teacher';
  const filename = `video-message_${safeTeacher}_${Date.now()}${extension}`;

  const policyData = await getOSSPolicy(filename, 'video-messages/');
  // 10 minutes — a 150MB clip on a slow phone connection is well past the
  // 120s default, and the signing window is 15 minutes to match.
  const fileUrl = await uploadToOSS(policyData, videoFile, { timeoutMs: 600000 });
  return { url: fileUrl, filename };
}

/** @returns {Promise<UploadResult>} */
export async function uploadAudioFile(audioFile, userId, courseName, bookNumber, lessonNumber, submissionType) {
  logger.log('[OSS uploadAudioFile] Called with:', {
    audioFileType: audioFile?.type,
    audioFileSize: audioFile?.size,
    userId, courseName, bookNumber, lessonNumber, submissionType
  });

  if (!audioFile || !userId || !submissionType) {
    throw new Error('Missing required parameters for file upload');
  }

  const extension = getExtensionFromMimeType(audioFile.type);
  logger.log('[OSS] Blob type:', audioFile.type, '-> extension:', extension);

  const safeType = sanitize(submissionType.toLowerCase(), true);
  const safeCourse = sanitize(courseName) || 'course';
  const safeBook = sanitize(bookNumber) || '0';
  const safeLesson = sanitize(lessonNumber) || '0';

  const filename = `${safeType}_${safeCourse}_${safeBook}_${safeLesson}_${Date.now()}${extension}`;
  logger.log('[OSS] Generated filename:', filename);

  if (filename.length > 255) {
    throw new Error('Generated filename is too long');
  }

  const userDir = `submissions/${userId}/`;

  logger.log('[OSS] Requesting policy for:', { filename, userDir });
  const policyData = await getOSSPolicy(filename, userDir);
  logger.log('[OSS] Received policy:', policyData);

  const fileUrl = await uploadToOSS(policyData, audioFile);
  return { url: fileUrl, filename };
}
